import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDefaultLandingRoute } from '../hooks/useDefaultLandingRoute';
import { useFcmRegistration } from '../hooks/useFcmRegistration';
import { getAppContext } from '../utils/getAppContext';
import { getBrandConfig } from '../utils/brandConfig';

// Post-login landing for every brand: a branded entry whose org has the
// brand's feature goes to its module; everyone else lands like AskSmart.
export default function AuthRedirect() {
  const { fetchSession, postLoginDestination, entitlements } = useAuth();
  const { route, ready } = useDefaultLandingRoute();
  const navigate = useNavigate();
  const [sessionFetched, setSessionFetched] = useState(false);

  useFcmRegistration(sessionFetched);

  useEffect(() => {
    const run = async () => {
      const success = await fetchSession();
      if (!success) {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/logout`;
      } else {
        setSessionFetched(true);
      }
    };
    run();
  }, [fetchSession]);

  useEffect(() => {
    if (!sessionFetched) return;
    const brand = getBrandConfig(getAppContext());
    if (
      !brand.isDefault &&
      brand.entitlementFeatureKey &&
      brand.moduleRoute &&
      entitlements?.features?.[brand.entitlementFeatureKey]?.enabled === true
    ) {
      navigate(brand.moduleRoute);
      return;
    }
    if (postLoginDestination) {
      navigate(postLoginDestination);
      return;
    }
    if (!ready || !route) return;
    navigate(route);
  }, [sessionFetched, entitlements, postLoginDestination, ready, route, navigate]);

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        fontSize: '18px',
        fontWeight: 500,
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        aria-hidden="true"
        style={{
          width: 24,
          height: 24,
          border: '3px solid rgba(0, 0, 0, 0.15)',
          borderTopColor: '#218838',
          borderRadius: '50%',
          animation: 'auth-redirect-spin 0.6s linear infinite',
        }}
      />
      <span>Signing you in...</span>
      <style>{`@keyframes auth-redirect-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
